import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { AiAgentBundleId } from '@euler/model/ai-home';
import { ApdmSessionChecklistResult } from '@euler/model/apdm';
import { useServiceFactory } from '@euler/services/factory';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Text, View } from 'react-native';
import { AiChatView } from './chat/AiChatView';
import { AiChatConversation } from './conversation';
import { takeAiChatStart } from './openAiChat';
import { AiSuiteTool, aiColors, aiSuiteToolForSession } from './theme';

export interface AiChatPageParams {
  sessionId?: string;
  bundleId?: AiAgentBundleId;
  /** Names a start handed over through `openAiChat`. */
  requestId?: string;
}

function AiChat({ sessionId, bundleId, requestId }: AiChatPageParams) {
  const navigation = useAppNavigation();
  const local = useNavigation();
  const { aiAssistantService, apdmCampaignService } = useServiceFactory();
  const [tool, setTool] = useState<AiSuiteTool | null>(() =>
    bundleId ? aiSuiteToolForSession(bundleId) : null,
  );

  // The saved conversation becomes the route, so a reload or a shared
  // link reopens it.
  const sessionSaved = useCallback(
    (id: string) =>
      local.setParams({ sessionId: id, requestId: undefined } as never),
    [local],
  );
  const conversation = useMemo(
    () => new AiChatConversation(aiAssistantService, sessionSaved),
    [aiAssistantService, sessionSaved],
  );
  const [state] = useBehaviorSubject(conversation.state$);
  const [plan, setPlan] = useState<ApdmSessionChecklistResult | null>(null);
  const [tick, setTick] = useState(0);
  // Read again whenever a reply finishes: it may have settled a step. What
  // was read stays up while a reply runs, and goes with the conversation.
  const openSessionId = state.sessionId;
  const settled = openSessionId && !state.busy ? openSessionId : null;
  const replies = state.messages.length;
  useEffect(() => setPlan(null), [openSessionId]);
  useEffect(() => {
    if (!settled) return;
    let active = true;
    apdmCampaignService
      .planChecklist(settled)
      .then(result => {
        if (active) setPlan(result);
      })
      .catch(error => console.error('APDM plan checklist failed', error));
    return () => {
      active = false;
    };
  }, [apdmCampaignService, settled, replies, tick]);
  // A launched campaign moves on its own: its monitor reads it again every
  // minute while the conversation is open.
  const watching = !!plan?.campaign;
  useEffect(() => {
    if (!watching) return;
    const timer = setInterval(() => setTick(value => value + 1), 60_000);
    return () => clearInterval(timer);
  }, [watching]);
  useEffect(() => () => conversation.dispose(), [conversation]);

  useEffect(() => {
    const start = takeAiChatStart(requestId);
    if (start) {
      const focus = start.tool?.bundleId ?? start.bundleId;
      conversation.reset(focus);
      setTool(start.tool ?? (focus ? aiSuiteToolForSession(focus) : null));
      if (start.message)
        conversation.send(
          start.message.text,
          start.message.attachments,
          start.message.tap,
        );
      return;
    }
    if (sessionId && conversation.state$.value.sessionId !== sessionId)
      void conversation.open(sessionId, bundleId);
  }, [conversation, sessionId, bundleId, requestId]);

  const close = useCallback(() => navigation.goBack(), [navigation]);

  return (
    <View style={{ flex: 1, backgroundColor: aiColors.background }}>
      <AiChatView
        state={state}
        tool={tool}
        showBack
        onBack={close}
        onSend={(text, attachments, tap) =>
          conversation.send(text, attachments, tap)
        }
        onStop={() => conversation.stop()}
        onRetry={() => conversation.retry()}
        onReact={(key, reaction) => conversation.react(key, reaction)}
        onEarlier={() => void conversation.earlier()}
        checklist={plan?.checklist ?? null}
        settled={plan?.settled ?? null}
        campaign={plan?.campaign ?? null}
        tapSettled$={conversation.tapSettled$}
      />
    </View>
  );
}

/** The 知车 AI chat: a full screen over the tabs, like a task page. */
export const AiChatPage = wrapNavigatorScreen(
  (props: AiChatPageParams) => {
    const profile = useUserProfileInfo();
    if (!profile?.user.enabled)
      return <Text>当前账号不可用，请重新登录。</Text>;
    return (
      <AiChat
        key={`${profile.org.id}:${profile.store.id}:${profile.user.userId}`}
        {...props}
      />
    );
  },
  {
    title: '知车 AI',
    headerShown: false,
    // On web a stack card that fills the window lets the document scroll,
    // which takes the chat's own header away with the messages. A floating
    // header mode keeps the card to the window, so only the list scrolls.
    headerMode: 'float',
  },
);
